import { Search, Misuse } from '@carbon/icons-react';
import { CustomizedTypography } from '@components/Customized';
import { colorSet } from '@components/Provider';
import {
  TextField,
  IconButton,
  Select,
  MenuItem,
  MenuProps,
  makeStyles
} from '@material-ui/core';
import { useEffect, useState } from 'react';

interface SeachInputOption {
  label: string;
  value: string;
}
interface SearchInputProps {
  onSubmit: ({ keyword, tag }: { keyword: string; tag: string }) => void;
  options: SeachInputOption[];
}

const SearchInput = ({ onSubmit, options }: SearchInputProps) => {
  const { selectStyle } = useStyle();
  const [keyword, setKeyword] = useState('');
  const [tag, setTag] = useState(options[0]?.value ?? 'ALL');

  const handleReset = () => {
    setKeyword('');
  };

  const handleSubmit = () => {
    onSubmit({ keyword, tag });
  };

  const getLabelByValue = (targetValue: string) => {
    return options.find(({ value }) => value === targetValue)?.label ?? '';
  };

  useEffect(() => {
    handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tag]);

  return (
    <form
      style={{
        width: '100%'
      }}
    >
      <TextField
        fullWidth
        variant="outlined"
        value={keyword}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleSubmit();
          }
        }}
        onChange={(e) => {
          setKeyword(e.target.value);
        }}
        InputProps={{
          startAdornment: (
            <Search
              style={{
                cursor: 'pointer',
                minWidth: 20,
                minHeight: 20,
                width: 20,
                height: 20
              }}
              onClick={handleSubmit}
            />
          ),
          endAdornment: !!keyword.length && (
            <IconButton size="small" onClick={handleReset}>
              <Misuse color={colorSet.gray700} size={20} />
            </IconButton>
          )
        }}
      />
      <Select
        className={selectStyle}
        MenuProps={CustomMenuProps}
        value={tag}
        renderValue={(value) => {
          return (
            <CustomizedTypography variant="body2" color="textSecondary">
              {getLabelByValue(value as string)}
            </CustomizedTypography>
          );
        }}
        onChange={(e) => {
          setTag(e.target.value as string);
        }}
      >
        {(options ?? []).map(({ value, label }) => {
          return (
            <MenuItem key={value} value={value}>
              <CustomizedTypography variant="body2" fontWeight="500">
                {label}
              </CustomizedTypography>
            </MenuItem>
          );
        })}
      </Select>
    </form>
  );
};

export default SearchInput;

const useStyle = makeStyles(
  {
    selectStyle: {
      marginTop: 12,
      fontSize: 14,
      color: '#868E96',
      '&:before': {
        content: 'none'
      },
      '&:after': {
        content: 'none'
      },
      '& .MuiSelect-select:focus': {
        backgroundColor: 'transparent'
      }
    }
  },
  {
    classNamePrefix: 'SearchInput-'
  }
);

const CustomMenuProps: Partial<MenuProps> = {
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left'
  },
  getContentAnchorEl: null,
  PaperProps: {
    elevation: 0,
    style: {
      border: `${colorSet.gray400} 1px solid`,
      width: 144,
      borderRadius: 8,
      transform: 'translateY(-5px)'
    }
  }
};
